import { KNOWLEDGE_STATUSES, type KnowledgeStatus } from '@sbc/domain-models/knowledge-base';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useBulkApproveArticles, useKnowledgeTree } from '@/lib/api/knowledge-base';

import { StatusBadge, VisibilityBadge } from './kb-badges';

export type StatusFilter = KnowledgeStatus | 'all';

export const STATUS_FILTERS: StatusFilter[] = ['all', ...KNOWLEDGE_STATUSES];

// The tab opens as the approval queue, so an absent status means "drafts".
export const DEFAULT_STATUS_FILTER: StatusFilter = 'draft';

export function isStatusFilter(value: string): value is StatusFilter {
  return (STATUS_FILTERS as readonly string[]).includes(value);
}

// Radix Select has no empty-string value; this sentinel means "no owner filter".
const ALL_OWNERS = '__all__';

// Shared by the header "select all" and the per-row checkboxes so their styling
// stays in sync.
const CHECKBOX_CLASS = 'size-4 accent-[var(--color-primary)] disabled:opacity-40';

export interface KbAllArticlesSearch {
  owner?: string;
  status?: StatusFilter;
  q?: string;
}

export function KbAllArticlesPage({ owner, status, q }: KbAllArticlesSearch) {
  const treeQuery = useKnowledgeTree();
  const bulkApprove = useBulkApproveArticles();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [summary, setSummary] = useState<string | null>(null);

  const search = q ?? '';
  const effectiveStatus = status ?? DEFAULT_STATUS_FILTER;

  const setFilter = (patch: KbAllArticlesSearch) => {
    void navigate({
      to: '/knowledge-base/all',
      search: { owner, status, q, ...patch },
    });
  };

  const tree = treeQuery.data;
  const folderPathById = useMemo(() => {
    const map = new Map<string, string>();
    for (const folder of tree?.folders ?? []) {
      map.set(folder.id, folder.path);
    }
    return map;
  }, [tree]);

  const owners = useMemo(
    () => [...new Set((tree?.articles ?? []).map((a) => a.ownerEmail))].toSorted(),
    [tree],
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (tree?.articles ?? [])
      .filter((a) => effectiveStatus === 'all' || a.status === effectiveStatus)
      .filter((a) => owner === undefined || a.ownerEmail === owner)
      .filter((a) => term === '' || a.title.toLowerCase().includes(term))
      .toSorted((a, b) => a.title.localeCompare(b.title));
  }, [tree, effectiveStatus, owner, search]);

  const selectableIds = useMemo(
    () => rows.filter((a) => a.status === 'draft').map((a) => a.id),
    [rows],
  );
  const selectedVisible = selectableIds.filter((id) => selected.has(id));
  const allSelected = selectableIds.length > 0 && selectedVisible.length === selectableIds.length;
  const someSelected = selectedVisible.length > 0 && !allSelected;

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  };

  const approveSelected = () => {
    setSummary(null);
    bulkApprove.mutate(selectedVisible, {
      onSuccess: (result) => {
        setSelected(new Set());
        setSummary(
          `${result.approved.length} approved${
            result.failed.length ? `, ${result.failed.length} failed` : ''
          }.`,
        );
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">All articles</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Every article across folders — filter, review, and bulk-approve drafts.
        </p>
      </div>

      {treeQuery.isPending && <p className="text-sm">Loading…</p>}
      {treeQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load the knowledge base: {treeQuery.error.message}
        </p>
      )}

      {tree && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-muted-foreground)]">Owner</span>
              <Select
                value={owner ?? ALL_OWNERS}
                onValueChange={(value) =>
                  setFilter({ owner: value === ALL_OWNERS ? undefined : value })
                }
              >
                <SelectTrigger className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_OWNERS}>All owners</SelectItem>
                  {owners.map((email) => (
                    <SelectItem key={email} value={email}>
                      {email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-muted-foreground)]">Status</span>
              <Select
                value={effectiveStatus}
                onValueChange={(value) => {
                  if (isStatusFilter(value)) {
                    setFilter({ status: value });
                  }
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {KNOWLEDGE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-[var(--color-muted-foreground)]">Search title</span>
              <Input
                value={search}
                placeholder="Search by title…"
                onChange={(e) =>
                  setFilter({ q: e.target.value === '' ? undefined : e.target.value })
                }
                className="max-w-xs"
              />
            </div>
          </div>

          {selectedVisible.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-md border bg-[var(--color-muted)] px-3 py-2 text-sm">
              <span className="font-medium">{selectedVisible.length} selected</span>
              <Button
                type="button"
                size="sm"
                disabled={bulkApprove.isPending}
                onClick={approveSelected}
              >
                {bulkApprove.isPending ? 'Approving…' : 'Approve'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={bulkApprove.isPending}
                onClick={() => setSelected(new Set())}
              >
                Clear
              </Button>
            </div>
          )}

          {summary && <p className="text-sm text-[var(--color-muted-foreground)]">{summary}</p>}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      aria-label="Select all draft articles"
                      className={CHECKBOX_CLASS}
                      disabled={selectableIds.length === 0}
                      checked={allSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = someSelected;
                        }
                      }}
                      onChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Folder</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length ? (
                  rows.map((article) => {
                    const isDraft = article.status === 'draft';
                    return (
                      <TableRow key={article.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            aria-label={`Select ${article.title}`}
                            className={CHECKBOX_CLASS}
                            disabled={!isDraft}
                            title={isDraft ? undefined : 'Only draft articles can be approved'}
                            checked={selected.has(article.id)}
                            onChange={() => toggleRow(article.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            to="/knowledge-base/articles/$articleId/edit"
                            params={{ articleId: article.id }}
                            className="font-medium hover:underline"
                          >
                            {article.title}
                          </Link>
                          <span className="block text-xs text-[var(--color-muted-foreground)]">
                            {article.slug}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-[var(--color-muted-foreground)]">
                          {folderPathById.get(article.folderId) ?? '—'}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={article.status} />
                        </TableCell>
                        <TableCell>
                          <VisibilityBadge visibility={article.visibility} />
                        </TableCell>
                        <TableCell className="text-sm">{article.ownerEmail}</TableCell>
                        <TableCell className="text-sm">
                          {new Date(article.updatedAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No articles match these filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
